import { useState } from 'react';

interface Props {
  onSuccess: () => void;
}

type Mode = 'login' | 'register';

export default function SellerAuthModal({ onSuccess }: Props) {
  const [mode, setMode] = useState<Mode>('login');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    shopName: '',
    contactName: '',
    phone: '',
    email: '',
    password: '',
    businessLicense: '',
    category: 'stationery',
    bankCard: '',
  });

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'register' && step === 1) {
      setStep(2);
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onSuccess();
    }, 1500);
  };

  return (
    <div className="bg-white rounded-3xl w-full max-w-md p-8">
      {/* Logo */}
      <div className="text-center mb-6">
        <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-2xl mx-auto mb-3">
          <i className="ri-store-3-line text-white text-2xl"></i>
        </div>
        <h1 className="text-2xl font-black text-gray-900">卖家工作台</h1>
        <p className="text-gray-400 text-sm mt-1">工厂/商家专属入口</p>
      </div>

      {/* Mode Switch */}
      <div className="flex bg-gray-100 rounded-full px-1 py-1 mb-6">
        <button
          onClick={() => { setMode('login'); setStep(1); }}
          className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'login' ? 'bg-red-600 text-white' : 'text-gray-500'}`}
        >
          卖家登录
        </button>
        <button
          onClick={() => { setMode('register'); setStep(1); }}
          className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'register' ? 'bg-red-600 text-white' : 'text-gray-500'}`}
        >
          注册入驻
        </button>
      </div>

      {mode === 'register' && (
        <div className="flex items-center gap-2 mb-5">
          <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
          <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
          <span className="text-xs text-gray-400 whitespace-nowrap">步骤 {step}/2</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {mode === 'login' && (
          <>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">手机号 / 邮箱</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-user-line text-gray-400 text-sm"></i>
                <input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  placeholder="请输入手机号或邮箱"
                  className="flex-1 outline-none text-sm"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">密码</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-lock-line text-gray-400 text-sm"></i>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={e => handleChange('password', e.target.value)}
                  placeholder="请输入密码"
                  className="flex-1 outline-none text-sm"
                  required
                />
              </div>
            </div>
          </>
        )}

        {mode === 'register' && step === 1 && (
          <>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">店铺名称</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-store-3-line text-gray-400 text-sm"></i>
                <input type="text" name="shopName" value={form.shopName} onChange={e => handleChange('shopName', e.target.value)} placeholder="如：晨光文具旗舰店" className="flex-1 outline-none text-sm" required />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">联系人姓名</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-user-line text-gray-400 text-sm"></i>
                <input type="text" name="contactName" value={form.contactName} onChange={e => handleChange('contactName', e.target.value)} placeholder="请输入真实姓名" className="flex-1 outline-none text-sm" required />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">手机号</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-phone-line text-gray-400 text-sm"></i>
                <input type="tel" name="phone" value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="请输入手机号" className="flex-1 outline-none text-sm" required />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">主营类目</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-apps-line text-gray-400 text-sm"></i>
                <select value={form.category} onChange={e => handleChange('category', e.target.value)} className="flex-1 outline-none text-sm bg-transparent cursor-pointer">
                  <option value="stationery">文具/钢笔</option>
                  <option value="books">教辅书籍</option>
                  <option value="clothing">学生服装</option>
                  <option value="digital">学习设备</option>
                  <option value="desk">学习桌椅</option>
                </select>
              </div>
            </div>
          </>
        )}

        {mode === 'register' && step === 2 && (
          <>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">营业执照号</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-file-text-line text-gray-400 text-sm"></i>
                <input type="text" name="businessLicense" value={form.businessLicense} onChange={e => handleChange('businessLicense', e.target.value)} placeholder="请输入营业执照统一社会信用代码" className="flex-1 outline-none text-sm" required />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">邮箱</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-mail-line text-gray-400 text-sm"></i>
                <input type="email" name="email" value={form.email} onChange={e => handleChange('email', e.target.value)} placeholder="请输入邮箱" className="flex-1 outline-none text-sm" required />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">设置密码</label>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-lock-line text-gray-400 text-sm"></i>
                <input type="password" name="password" value={form.password} onChange={e => handleChange('password', e.target.value)} placeholder="至少8位，含字母和数字" className="flex-1 outline-none text-sm" required />
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-700 flex items-start gap-1.5">
                <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
                提交后平台将在1-3个工作日内完成审核，审核通过后即可上传商品开始销售。
              </p>
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60 mt-2"
        >
          {loading ? '处理中...' : mode === 'login' ? '登录工作台' : step === 1 ? '下一步' : '提交申请'}
        </button>
      </form>

      <p className="text-center text-xs text-gray-400 mt-4">
        注册即代表同意
        <span className="text-red-600 cursor-pointer hover:underline mx-1">《卖家服务协议》</span>
        和
        <span className="text-red-600 cursor-pointer hover:underline mx-1">《平台规则》</span>
      </p>
    </div>
  );
}
